import { Employee } from "@finway-group/shared/lib/models"
import { Button, Col, Form, Modal, Row } from "antd"
import { Store } from "antd/lib/form/interface"
import moment from "moment"
import React from "react"
import { Trans, useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import EmployeeLink from "~Components/employeeLink"
import { EmployeeAbsenceForm } from "~Components/forms/employeeAbsence.from"
import { CancelableComponent, ModalComponentPropsBase } from "~Shared/context/modal.context"
import { useEmployees } from "~Shared/hooks/employee.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { AuthzService, NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { updateEmployeeAbsence } from "~Shared/store/actions/employee/employeeActions"
import useStateIfMounted from "~Shared/utils/hooks/useStateIfMounted"

interface EmployeeAbsenceModalProps extends CancelableComponent, ModalComponentPropsBase {
    employee: Employee
}

export const EmployeeAbsenceModal = ({ isShowing, onCancel = () => {}, employee }: EmployeeAbsenceModalProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { id } = employee
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const employees = useEmployees({ excludeDeleted: true })
    const substitutes = employee?.activeCompanyProfile?.substitutes || []

    // prettier-ignore
    const substituteUsers: Array<Employee> = substitutes
        .map((substituteItem) => employees.find((employeeItem) => employeeItem.id === substituteItem.userId))
        .filter(Boolean) as Array<Employee>

    const [formInstance] = Form.useForm()

    const handleSubmit = async (values: Store) => {
        const [startDate, endDate] = values.absenceDate
        try {
            await dispatch(updateEmployeeAbsence(id, { startDate, endDate }))
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.absence_updated.title"), t("notification:employee.absence_updated.message"))
            handleCancel()
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee_absence.update.title"))
        }
    }

    const handleRemoveAbsence = async () => {
        try {
            const yesterday = moment().subtract(1, "day")
            await dispatch(updateEmployeeAbsence(id, { startDate: yesterday, endDate: yesterday }))
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.absence_removed.title"), t("notification:employee.absence_removed.message"))
            handleCancel()
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee_absence.update.title"))
        }
    }

    const handleCancel = () => {
        setIsLoading(false)
        onCancel(false)
    }

    return (
        <Modal
            title={t("action:employee_absence.set_up")}
            open={isShowing}
            maskClosable={false}
            onOk={formInstance.submit}
            onCancel={handleCancel}
            keyboard
            destroyOnClose
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        formInstance.resetFields()
                        onCancel(false)
                    }}
                >
                    {t("action:cancel")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isLoading}
                    onClick={() => {
                        formInstance.submit()
                    }}
                >
                    {t("action:save")}
                </Button>,
            ]}
        >
            <EmployeeAbsenceForm formInstance={formInstance} onSubmit={handleSubmit} handleRemoveAbsence={handleRemoveAbsence} employee={employee} />
            <SubstituteSection employeeId={id} substituteUsers={substituteUsers} onCancel={handleCancel} />
        </Modal>
    )
}

const SubstituteSection = ({ employeeId, substituteUsers, onCancel }: { employeeId: string; substituteUsers: Array<Employee>; onCancel: () => void }) => {
    const loggedInEmployee = useLoggedInEmployeeProfile()
    const isSelf = employeeId === loggedInEmployee?.id
    const { t } = useTranslation()
    if (isSelf && !AuthzService.canSeeAbsenceManagementSettings()) {
        return <></>
    }

    if (substituteUsers.length) {
        return (
            <>
                <span className="text-text">{t("info:employee_absence_role.substitutes_set_up")}</span>
                <Row className="mt-16">
                    <Col span={12}>
                        <Row>{t("info:employee_absence.modal.primary_substitute")}:</Row>
                        <Row>
                            <span onClick={onCancel} className="truncate max-w-200">
                                <EmployeeLink employee={substituteUsers[0]} />
                            </span>
                        </Row>
                    </Col>
                    <Col span={12}>
                        <Row>{t("info:employee_absence.modal.secondary_substitute")}:</Row>
                        <Row className="items-center">
                            {substituteUsers.length < 2 ? (
                                <span className="text-text">{t("info:employee_absence.modal.no_secondary_substitute_set")}</span>
                            ) : (
                                <span onClick={onCancel} className="truncate max-w-200">
                                    <EmployeeLink employee={substituteUsers[1]} />
                                </span>
                            )}
                        </Row>
                    </Col>
                </Row>
            </>
        )
    }

    return (
        <span className="text-text">
            {t("info:employee_absence_role.substitutes_not_set_up")}
            {isSelf && (
                <Trans
                    t={t}
                    i18nKey="info:employee_absence_role.substitutes_set_up_link"
                    components={[<Link key="1" to={RoutePathEnum.SETTINGS_USER_ABSENCE_MANAGEMENT} target="_blank" rel="noreferrer" className="text-blue underline" />]}
                />
            )}
        </span>
    )
}
